import styled from '@emotion/styled';
import Button from '@base/Button/Button';
import Paper from '@base/Paper/Paper';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import React from 'react';
import { emptyObject } from '@constants/empty';
import EducatorAvatar from '@cont/Practice/AutoDpp/EducatorAvatar';
import AvatarGroup from '@cont/Practice/AutoDpp/AvatarGroup';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import {
  DPP_COMPLETED,
  DPP_INCOMPLETE,
  PRACTICE_FLOW_TYPE,
  PRACTICE_TYPE
} from '@cont/Practice/constants';
import useFinishSummary from '@stores/auto-dpp/dppFinishSummaryStore';
import usePracticeSegmentAnalytics from '@cont/Practice/usePracticeSegmentAnalytics';
import Image from '@base/Image';
import { useDispatch } from 'react-redux';
import { finishPracticeSession } from '@actions/practiceSession';
import pushRoute from '@utils/pushRoute';
import { useRouter } from 'next/router';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import { nFormatter } from '@utils/textHelper';
import usePracticeStore from '@stores/planner/practiceStore';
import { MIN_QUIZ_ATTEMPTS, PRACTICE_STARTED } from '../constants';

const OuterWrapper = styled(Paper)`
  width: 100%;
  background-color: var(--color-base-1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: ${boxShadowGenerator()};
`;

const TopicGroupTag = styled.div`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  padding: 0;
  width: 100%;
  line-height: ${Spacings.SPACING_3B};
  text-transform: uppercase;
  font-size: ${Fonts.ULTRA_SMALL};
  font-weight: ${Fonts.HEAVY};
  margin-bottom: ${Spacings.SPACING_6};
  max-width: none;
  color: ${({ color }) => color};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.EXTRA_SMALL};
  }
`;

const Details = styled.div`
  margin-right: ${Spacings.SPACING_1B};
  width: 65%;
`;

const StyledButton = styled(Button)`
  border-radius: ${Spacings.SPACING_1B};
  font-size: ${Fonts.SMALL};
  height: ${Spacings.SPACING_10B};
  color: ${({ $isDarkModeOn, $textColor }) =>
    $isDarkModeOn ? $textColor : ''};
`;

const ActionWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  margin-top: ${Spacings.SPACING_4B};
`;

const StyledTitle = styled.div`
  font-size: ${Fonts.LARGE};
  font-weight: ${Fonts.SEMIBOLD};
  max-width: 280px;
  line-height: ${Spacings.SPACING_5B};
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: ${Spacings.SPACING_2};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  color: var(--color-text-primary);
`;

const StyledDetail = styled.div`
  margin-top: ${({ $marginTop }) => $marginTop ?? 0};
  color: var(--color-text-secondary);
  font-size: ${Fonts.ULTRA_SMALL};
  line-height: 18px;
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
`;

const AttemptCountInfo = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_4B};
`;

const DppCard = ({ practice, isSubscribed, isLoggedIn = false }) => {
  const {
    classDetails,
    educatorDetails,
    sessionDetails,
    numQuestions,
    totalQuizAttempts,
    goalUid: goalUID,
    uid: quizUID,
    goalSlug
  } = practice ?? emptyObject;

  const router = useRouter();
  const { topicGroupName, className, topicGroupUid, topicColor } = classDetails;
  const { avatar, firstName, lastName } = educatorDetails ?? emptyObject;
  const { solvedQuestions, status, sessionUid } = sessionDetails ?? emptyObject;

  const { toggleShowSummary, setFinishSummary } = useFinishSummary();
  const { finishPlannerPracticeSession } = usePracticeStore();
  const segmentAnalytics = usePracticeSegmentAnalytics(goalUID);
  const dispatch = useDispatch();
  const isDarkMode = useDarkModeStatus(getIsDarkModeOn);

  const isCompleted = status === DPP_COMPLETED;
  const isInComplete = status === DPP_INCOMPLETE;

  const handleButtonClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLoggedIn || !isSubscribed) {
      return router.push(
        {
          pathname: '/goal/[goalSlug]/[goalUID]/subscribe'
        },
        `/goal/${goalSlug}/${goalUID}/subscribe`
      );
    }

    if (isCompleted && sessionUid) {
      finishPlannerPracticeSession({}, false);
      await dispatch(finishPracticeSession(sessionUid));
      setFinishSummary({
        goalSlug,
        goalUID,
        sessionId: sessionUid,
        topicGroup: topicGroupUid,
        topicUID: topicGroupUid,
        quizUID,
        type: PRACTICE_FLOW_TYPE.AUTO_DPP,
        educatorName: `${firstName} ${lastName}`
      });
      return toggleShowSummary();
    }
    if (quizUID && topicGroupUid && goalUID && goalSlug) {
      return pushRoute({
        pathname: '/goal/[goalSlug]/[goalUID]/practice/[topicUID]',
        query: {
          goalSlug,
          goalUID,
          topic_type: PRACTICE_TYPE.EACH_TOPIC,
          type: PRACTICE_FLOW_TYPE.AUTO_DPP,
          topic_group: topicGroupUid,
          topicUID: topicGroupUid,
          quiz_uid: quizUID,
          educator_name: `${firstName} ${lastName}`,
          source: 'Educator Profile'
        }
      }).then(() =>
        segmentAnalytics(PRACTICE_STARTED, {
          topic_uid: null,
          practice_questions: numQuestions,
          topology_id: topicGroupUid,
          topology_name: 'NA',
          topology_level: 'NA',
          last_primary_source: 'Educator Profile',
          language: 'NA',
          practice_type: 'Educator Led Practice',
          is_completed: isCompleted ? 'Yes' : 'No'
        })
      );
    }
  };

  const getAttemptText = () => {
    if (isCompleted) {
      return `${solvedQuestions} of ${numQuestions} completed`;
    }
    if (isInComplete && sessionUid && solvedQuestions >= 1) {
      return `${solvedQuestions} of ${numQuestions} attempted`;
    }
    return `${numQuestions} questions`;
  };

  const getBtnLabel = () => {
    if (!isLoggedIn || !isSubscribed) {
      return 'Get subscription';
    }
    if (isCompleted) {
      return 'View';
    }
    if (isInComplete && sessionUid && solvedQuestions >= 1) {
      return 'Resume';
    }
    return 'Start';
  };

  const getBtnVariant = () => {
    if (
      !isLoggedIn ||
      !isSubscribed ||
      (isInComplete && solvedQuestions === 0)
    ) {
      return 'filled';
    }
    return 'hollow';
  };

  const getAttemptCountText = (attemptCount) => {
    const attempts = Number(attemptCount);
    if (attempts < 10) {
      return 'Be the first to attempt';
    }
    return `${nFormatter(attempts, 1)} attempted`;
  };

  const getTextColorForDarkMode = () => {
    if (
      (isInComplete && solvedQuestions === 0) ||
      !isLoggedIn ||
      !isSubscribed
    ) {
      return 'var(--color-i-black)';
    }
    return 'var(--color-i-gray-9)';
  };

  return (
    <OuterWrapper padding={Spacings.SPACING_4B}>
      <Flex>
        <Details>
          <TopicGroupTag color={topicColor}>{topicGroupName}</TopicGroupTag>
          <StyledTitle>{className}</StyledTitle>

          <StyledDetail $marginTop={Spacings.SPACING_1B}>
            {getAttemptText()}
          </StyledDetail>
        </Details>
        {isLoggedIn && isSubscribed ? (
          <EducatorAvatar avatar={avatar} circleColor={topicColor} />
        ) : (
          <div>
            <Image width="56" height="56" src="educator/lock.svg" alt="lock" />
          </div>
        )}
      </Flex>

      <ActionWrapper>
        <StyledButton
          $isDarkModeOn={isDarkMode}
          variant={getBtnVariant()}
          color="black"
          label={getBtnLabel()}
          $textColor={getTextColorForDarkMode()}
          width="100%"
          maxHeight="sm"
          onClick={handleButtonClick}
        />
        <AttemptCountInfo>
          {Number(totalQuizAttempts) >= MIN_QUIZ_ATTEMPTS && <AvatarGroup />}
          <StyledDetail>{getAttemptCountText(totalQuizAttempts)}</StyledDetail>
        </AttemptCountInfo>
      </ActionWrapper>
    </OuterWrapper>
  );
};

export default DppCard;
